import { AddressV2, GiftCard, IProxy, ScoreCard } from '@cart-checkout/checkout/data-access/interfaces';
import { Checkout, CommerceAddress, IGift } from '@cart-checkout/data-access/interfaces';
import { CustomHttpHeaders, MediaType } from '@shared/data-access/enums';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, retry, retryWhen } from 'rxjs/operators';

import { CheckoutFlow } from '@cart-checkout/checkout/data-access/enums';
import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import { Injectable } from '@angular/core';
import { Logger } from '@logger';
import { httpScalingRetryStrategy } from '@shared/utils/services';

@Injectable({
	providedIn: 'root'
})
export class CheckoutDao {
	constructor(private http: HttpClient, private logger: Logger) {}

	// Default HTTP Header properties
	private DEFAULT_HTTP_HEADER_PROPERTIES = {
		'Accept': MediaType.APPLICATION_JSON,
		'Content-Type': MediaType.APPLICATION_JSON,
		'Access-Control-Allow-Origin': '*',
		'Cache-Control': 'no-cache,no-store,must-revalidate,max-age=0',
		'Pragma': 'no-cache',
		'Expires': 'Sat, 01 Jan 2000 00:00:00 GMT'
	};

	// Default HTTP Headers object
	private DEFAULT_HTTP_HEADERS = new HttpHeaders(this.DEFAULT_HTTP_HEADER_PROPERTIES);

	// Default number of HTTP retries
	private DEFAULT_HTTP_RETRIES = 2;

	// Default scaling duration in milliseconds for HTTP Retries
	private DEFAULT_HTTP_RETRY_DURATION = 1000;

	//region Legacy Checkout

	/**
	 * @returns {Observable<Checkout>}
	 *
	 * @description
	 * Create a full checkout object
	 */
	public createCheckout$(version: 'v1' | 'v2' = 'v1'): Observable<Checkout> {
		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		// Override default Timeout specified in HttpInterceptor
		httpOptions.headers = httpOptions.headers.set(CustomHttpHeaders.X_TIMEOUT, `${ DCSG_CONSTANTS.timeouts.medium }`);

		// Make the HTTP Request
		return this.http.post<Checkout>(`/api/${version}/checkouts`, httpOptions);
	}

	/**
	 * @returns {Observable<Checkout>}
	 *
	 * @description
	 * Gets an existing checkout object by checkout key. Accepts an argument to
	 * distinguish between pre/post-order submit to append a query parameter
	 * to distinguish between the two. If the order has already been submitted
	 * and the query parameter is not in the URL, then a 410 error will be returned
	 *
	 * @param checkoutKey
	 * @param submitted
	 */
	public getCheckout$(checkoutKey: string, submitted = false, version: 'v1' | 'v2' = 'v1'): Observable<Checkout> {
		// Default URL
		let url = `/api/${version}/checkouts/${checkoutKey}`;

		// Check if submitted flag is true so that we can append the submitted query parameter
		if (submitted === true) {
			url = `${ url }?submitted=true`;
		}

		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		// Override default Timeout specified in HttpInterceptor
		httpOptions.headers = httpOptions.headers.set(CustomHttpHeaders.X_TIMEOUT, `${ DCSG_CONSTANTS.timeouts.medium }`);

		// Make the HTTP Request
		return this.http.get<Checkout>(url, httpOptions).pipe(
			retry(this.DEFAULT_HTTP_RETRIES)
		);
	}

	/**
	 * @returns {Observable<Checkout>}
	 *
	 * @description
	 * Creates a slimmed-down checkout object with only the order summary
	 */
	public createCheckoutWithOnlyOrderSummary$(): Observable<Checkout> {
		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		// Override default Timeout specified in HttpInterceptor
		httpOptions.headers = httpOptions.headers.set(CustomHttpHeaders.X_TIMEOUT, `${ DCSG_CONSTANTS.timeouts.medium }`);

		// Make the HTTP Request
		return this.http.post<Checkout>('/api/v1/checkouts/order-summary', httpOptions);
	}

	/**
	 * @returns {Observable<Checkout>}
	 *
	 * @description
	 * Gets an existing slimmed-down checkout object with only the order summary
	 *
	 * @param checkoutKey
	 */
	public getCheckoutWithOnlyOrderSummary$(checkoutKey: string): Observable<Checkout> {
		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		// Override default Timeout specified in HttpInterceptor
		httpOptions.headers = httpOptions.headers.set(CustomHttpHeaders.X_TIMEOUT, `${ DCSG_CONSTANTS.timeouts.medium }`);

		// Make the HTTP Request
		return this.http.get<Checkout>(`/api/v1/checkouts/${ checkoutKey }/order-summary`, httpOptions);
	}

	/**
	 * @returns {Observable<string>}
	 *
	 * @description
	 * Gets a PayPal token for initiating a PayPal checkout
	 *
	 * NOTE: Retry strategy will ignore the following status codes and not perform any retries:
	 * 400 (Bad Request), 410 (Checkout Already Submitted), 422 (Checkout Redirect Required), 426 (Invalid Checkout Key)
	 *
	 * @param checkoutKey
	 */
	public getPaypalExpressToken$(checkoutKey: string): Observable<string> {
		// HTTP Headers
		const httpHeaders = new HttpHeaders({
			'Accept': 'text/html;charset=utf-8',
			'Content-Type': 'text/html;charset=utf-8',
			'Access-Control-Allow-Origin': '*',
			'Cache-Control': 'no-cache,no-store,must-revalidate,max-age=0',
			'Pragma': 'no-cache',
			'Expires': 'Sat, 01 Jan 2000 00:00:00 GMT'
		});

		// HTTP Options
		const httpOptions = {
			headers: httpHeaders,
			responseType: 'text' as const,
			withCredentials: true
		};

		// Make the HTTP Request
		return this.http.get(`/api/v1/checkouts/${ checkoutKey }/payment/payment-processor/paypal/public-key`, httpOptions).pipe(
			map((paypalResponse: string) => {
				return paypalResponse;
			}),
			retryWhen(httpScalingRetryStrategy({
				maxRetryAttempts: this.DEFAULT_HTTP_RETRIES,
				scalingDuration: this.DEFAULT_HTTP_RETRY_DURATION,
				excludedStatusCodes: [400, 410, 422, 426],
				logger: this.logger
			}))
		);
	}

	/**
	 * @returns {Observable<any>}
	 *
	 * @description
	 * Handles processing PayPal upon return to checkout from PayPal
	 *
	 * NOTE: Retry strategy will ignore the following status codes and not perform any retries:
	 * 400 (Bad Request), 410 (Checkout Already Submitted), 422 (Checkout Redirect Required), 426 (Invalid Checkout Key), 451 (Shipping Restriction)
	 *
	 * @param checkoutKey
	 * @param clientIp
	 */
	public processPaypalReturn$(checkoutKey: string, clientIp?: string): Observable<any> {
		// Default URL
		let url = `/api/v1/checkouts/${ checkoutKey }/payment/payment-processor/paypal`;

		// Check to see if the IP address is defined and append it if so
		if (typeof clientIp === 'string' && clientIp.trim() !== '') {
			url = `${ url }?client-ip=${ clientIp }`;
		}

		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		// Override default Timeout specified in HttpInterceptor
		httpOptions.headers = httpOptions.headers.set(CustomHttpHeaders.X_TIMEOUT, `${ DCSG_CONSTANTS.timeouts.long }`);

		// Make the HTTP Request
		return this.http.put<any>(url, null, httpOptions).pipe(
			retryWhen(httpScalingRetryStrategy({
				maxRetryAttempts: this.DEFAULT_HTTP_RETRIES,
				scalingDuration: this.DEFAULT_HTTP_RETRY_DURATION,
				excludedStatusCodes: [400, 410, 422, 426, 451],
				logger: this.logger
			}))
		);
	}

	/**
	 * @returns {Observable<string>}
	 *
	 * @description
	 * Gets promotions for the current items in the cart for the checkout object
	 *
	 * @param checkoutKey
	 * @param promotionId
	 */
	public getPromotionDescription(checkoutKey: string, promotionId: number): Observable<string> {
		// HTTP Headers
		const httpHeaders = new HttpHeaders({
			'Accept': 'text/html;charset=utf-8',
			'Content-Type': 'text/html;charset=utf-8',
			'Access-Control-Allow-Origin': '*',
			'Cache-Control': 'no-cache,no-store,must-revalidate,max-age=0',
			'Pragma': 'no-cache',
			'Expires': 'Sat, 01 Jan 2000 00:00:00 GMT'
		});

		// HTTP Options
		const httpOptions = {
			headers: httpHeaders,
			responseType: 'text' as const,
			withCredentials: true
		};

		// Override default Timeout specified in HttpInterceptor
		httpOptions.headers = httpOptions.headers.set(CustomHttpHeaders.X_TIMEOUT, `${ DCSG_CONSTANTS.timeouts.xshort }`);

		// Make the HTTP Request
		return this.http.get(`/api/v1/checkouts/${ checkoutKey }/promotion/cart/promotions/${ promotionId }/description`, httpOptions);
	}

	/**
	 * @returns {Observable<any>}
	 *
	 * @description
	 * Calls the checkout-engine to apply a promotion or reward code
	 *
	 * @param checkoutKey
	 * @param promoOrRewardCode
	 */
	public applyPromotionOrRewardCode$(checkoutKey: string, promoOrRewardCode: string, checkoutFlow: string): Observable<any> {
		promoOrRewardCode = promoOrRewardCode?.trim();
		if (!promoOrRewardCode) {
			return throwError({
				status: 'Fail',
				error:
					{
						message: "Missing promotion code."
					}
			});
		}

		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		if (checkoutFlow === CheckoutFlow.MODERN) {
			httpOptions.headers = this.setDefaultModernHttpHeaders(httpOptions);
			return this.http.put(`/api/v1/checkouts/${ checkoutKey }/promotion/cart/${ encodeURIComponent(promoOrRewardCode) }`, undefined, httpOptions);
		}
		// Make the HTTP Request
		return this.http.put(`/api/v1/checkouts/${ checkoutKey }/promotion/cart/promotions`, promoOrRewardCode, httpOptions);
	}

	/**
	 * @returns {Observable<any>}
	 *
	 * @description
	 * Calls the checkout-engine to remove a promotion or reward code
	 *
	 * @param checkoutKey
	 * @param promoOrRewardCode
	 */
	public removePromotionOrRewardCode$(checkoutKey: string, promoOrRewardCode: string, checkoutFlow: string): Observable<any> {
		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		if (checkoutFlow === CheckoutFlow.MODERN) {
			httpOptions.headers = this.setDefaultModernHttpHeaders(httpOptions);
			return this.http.delete(`/api/v1/checkouts/${ checkoutKey }/promotion/cart/${ promoOrRewardCode }`, httpOptions);
		}
		// Make the HTTP Request
		return this.http.delete(`/api/v1/checkouts/${ checkoutKey }/promotion/cart/promotions/${ promoOrRewardCode }`, httpOptions);
	}

	/**
	 * @returns {Observable<Checkout>}
	 *
	 * @description
	 * Function that calls the Checkout Engine add addresses API.  This coarse-grain API can accept an empty Object {}, or
	 * an Object with User address {'address': 'STRING', 'city': 'STRING', 'state': 'STRING', 'zipcode': 'STRING', 'country': 'STRING', 'phone': 'STRING', 'email': 'STRING', 'external_key': 'STRING', 'external_source': 'WCSAPI' }.
	 *
	 * NOTE: Retry strategy will ignore the following status codes and not perform any retries:
	 * 400 (Bad Request), 410 (Checkout Already Submitted), 422 (Checkout Redirect Required), 426 (Invalid Checkout Key)
	 *
	 * @param checkoutKey
	 * @param addressType
	 * @param userAddress
	 */
	public addAddress$ = (checkoutKey: string, addressType: string, userAddress: CommerceAddress): Observable<Checkout> => {
		// Default URL
		let url = `/api/v1/checkouts/${ checkoutKey }/addresses`;

		// Check the address type if it is defined to specify which exact type
		if (addressType === 'billing') {
			url = `${ url }?type=billing`;
		} else if (addressType === 'shipping') {
			url = `${ url }?type=shipping`;
		}

		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		// Make the HTTP Request
		return this.http.put<Checkout>(url, userAddress, httpOptions).pipe(
			retryWhen(httpScalingRetryStrategy({
				maxRetryAttempts: this.DEFAULT_HTTP_RETRIES,
				scalingDuration: this.DEFAULT_HTTP_RETRY_DURATION,
				excludedStatusCodes: [400, 410, 422, 426],
				logger: this.logger
			}))
		);
	};

	/**
	 * @returns {Observable<Checkout>}
	 *
	 * @description
	 * Function that calls the Checkout Engine integrateCheckoutAddresses API to sync addresses to commerce.
	 * This coarse-grain API accepts CheckoutKey as path parameter
	 *
	 * NOTE: Retry strategy will ignore the following status codes and not perform any retries:
	 * 400 (Bad Request), 410 (Checkout Already Submitted), 422 (Checkout Redirect Required), 426 (Invalid Checkout Key), 431 (ShipMode Restriction), 451 (Shipping Restriction)
	 *
	 * @param checkoutKey
	 */
	public finalizeAddresses$(checkoutKey: string): Observable<Checkout> {
		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		// Make the HTTP Request
		return this.http.post<Checkout>(`/api/v1/checkouts/${ checkoutKey }/addresses`, null, httpOptions).pipe(
			retryWhen(httpScalingRetryStrategy({
				maxRetryAttempts: this.DEFAULT_HTTP_RETRIES,
				scalingDuration: this.DEFAULT_HTTP_RETRY_DURATION,
				excludedStatusCodes: [400, 410, 422, 426, 431, 451],
				logger: this.logger
			}))
		);
	}

	/**
	 * @returns {Observable<Checkout>}
	 *
	 * @description
	 * Function that calls the checkout-engine payment-processor to update the payment
	 * on the checkout
	 *
	 * NOTE: Retry strategy will ignore the following status codes and not perform any retries:
	 * 400 (Bad Request), 402 (Payment Declined), 410 (Checkout Already Submitted), 422 (Checkout Redirect Required), 426 (Invalid Checkout Key), 428 (3DS Required)
	 *
	 * @param checkoutKey
	 * @param paymentRequest
	 */
	public updatePayment$(checkoutKey: string, paymentRequest: any): Observable<Checkout> {
		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		// Override default Timeout specified in HttpInterceptor
		httpOptions.headers = httpOptions.headers.set(CustomHttpHeaders.X_TIMEOUT, `${ DCSG_CONSTANTS.timeouts.long }`);

		// Make the HTTP Request
		return this.http.put<Checkout>(`/api/v1/checkouts/${ checkoutKey }/payment/payment-processor`, paymentRequest, httpOptions).pipe(
			retryWhen(httpScalingRetryStrategy({
				maxRetryAttempts: this.DEFAULT_HTTP_RETRIES,
				scalingDuration: this.DEFAULT_HTTP_RETRY_DURATION,
				excludedStatusCodes: [400, 402, 410, 422, 426, 428],
				logger: this.logger
			}))
		);
	}

	/**
	 * @returns {Observable<Checkout>}
	 *
	 * @description
	 * Finalizes/Submits the order
	 *
	 * NOTE: Retry strategy will ignore the following status codes and not perform any retries:
	 * 400 (Bad Request), 410 (Checkout Already Submitted), 422 (Checkout Redirect Required), 426 (Invalid Checkout Key)
	 *
	 * @param checkoutKey
	 * @param flow
	 */
	public finalizeCheckout$(checkoutKey: string, flow?: string): Observable<Checkout> {
		// Default URL
		let url = `/api/v1/checkouts/${ checkoutKey }`;

		// Check if the flow is specified, e.g. PayPal
		if (typeof flow === 'string' && flow.trim() !== '') {
			url = `${ url }?flow=${ flow }`;
		}

		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		// Override default Timeout specified in HttpInterceptor
		httpOptions.headers = httpOptions.headers.set(CustomHttpHeaders.X_TIMEOUT, `${ DCSG_CONSTANTS.timeouts.long }`);

		// Make the HTTP Request
		return this.http.post<Checkout>(url, null, httpOptions).pipe(
			retryWhen(httpScalingRetryStrategy({
				maxRetryAttempts: this.DEFAULT_HTTP_RETRIES,
				scalingDuration: this.DEFAULT_HTTP_RETRY_DURATION,
				excludedStatusCodes: [400, 410, 422, 426],
				logger: this.logger
			}))
		);
	}

	public finalizePaygateCheckout$(checkoutKey: string, flow?: string): Observable<Checkout> {
		// Default URL
		let url = `/api/v2/checkouts/${ checkoutKey }`;

		// Check if the flow is specified, e.g. PayPal
		if (typeof flow === 'string' && flow.trim() !== '') {
			url = `${ url }?flow=${ flow }`;
		}

		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		// Override default Timeout specified in HttpInterceptor
		httpOptions.headers = httpOptions.headers.set(CustomHttpHeaders.X_TIMEOUT, `${ DCSG_CONSTANTS.timeouts.long }`);

		// Make the HTTP Request
		return this.http.post<Checkout>(url, null, httpOptions).pipe(
			retryWhen(httpScalingRetryStrategy({
				maxRetryAttempts: this.DEFAULT_HTTP_RETRIES,
				scalingDuration: this.DEFAULT_HTTP_RETRY_DURATION,
				excludedStatusCodes: [400, 410, 422, 426],
				logger: this.logger
			}))
		);
	}

	/**
	 * @returns {Observable<Checkout>}
	 *
	 * @description
	 * Adds a gift card to the checkout
	 *
	 * NOTE: Retry strategy will ignore the following status codes and not perform any retries:
	 * 400 (Bad Request), 406 (The Gift Card Already Applied to the Order), 410 (Checkout Already Submitted), 417 ($0 Balance Error), 422 (Checkout Redirect Required), 426 (Invalid Checkout Key), 428 (3DS Required)
	 *
	 * @param checkoutKey
	 * @param giftCard
	 */
	public applyGiftCard(checkoutKey: string, giftCard: GiftCard): Observable<Checkout> {
		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		// Override default Timeout specified in HttpInterceptor
		httpOptions.headers = httpOptions.headers.set(CustomHttpHeaders.X_TIMEOUT, `${ DCSG_CONSTANTS.timeouts.short }`);

		// Make the HTTP Request
		return this.http.put<Checkout>(`/api/v1/checkouts/${ checkoutKey }/payment/gift-cards`, giftCard, httpOptions).pipe(
			retryWhen(httpScalingRetryStrategy({
				maxRetryAttempts: this.DEFAULT_HTTP_RETRIES,
				scalingDuration: this.DEFAULT_HTTP_RETRY_DURATION,
				excludedStatusCodes: [400, 406, 410, 417, 422, 426, 428],
				logger: this.logger
			}))
		);
	}

	/**
	 * @returns {Observable<Checkout>}
	 *
	 * @description
	 * Removes a gift card from the checkout
	 *
	 * NOTE: Retry strategy will ignore the following status codes and not perform any retries:
	 * 400 (Bad Request), 410 (Checkout Already Submitted), 422 (Checkout Redirect Required), 426 (Invalid Checkout Key)
	 * TODO -- Potentially add status codes after we start logging failures so we can capture additional HTTP status codes since we aren't capturing today
	 *
	 * @param checkoutKey
	 * @param giftCardKey
	 */
	public removeGiftCard(checkoutKey: string, giftCardKey: string): Observable<Checkout> {
		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		// Override default Timeout specified in HttpInterceptor
		httpOptions.headers = httpOptions.headers.set(CustomHttpHeaders.X_TIMEOUT, `${ DCSG_CONSTANTS.timeouts.short }`);

		// Make the HTTP Request
		return this.http.delete<Checkout>(`/api/v1/checkouts/${ checkoutKey }/payment/gift-cards/${ giftCardKey }`, httpOptions).pipe(
			retryWhen(httpScalingRetryStrategy({
				maxRetryAttempts: this.DEFAULT_HTTP_RETRIES,
				scalingDuration: this.DEFAULT_HTTP_RETRY_DURATION,
				excludedStatusCodes: [400, 410, 422, 426],
				logger: this.logger
			}))
		);
	}

	/**
	 * TODO -- REFACTOR TO USE THE FUNCTION #applyScoreCard INSTEAD OF SEPARATE CALL-THROUGH FUNCTIONS
	 * @param checkoutKey
	 * @param phoneNumber
	 */
	public applyScoreCardByPhone(checkoutKey: string, phoneNumber: string) {
		return this.applyScoreCard(`/api/v1/checkouts/${checkoutKey}/scorecard?phoneNumber=${phoneNumber}`);
	}

	/**
	 * TODO -- REFACTOR TO USE THE FUNCTION #applyScoreCard INSTEAD OF SEPARATE CALL-THROUGH FUNCTIONS
	 * @param checkoutKey
	 * @param scoreCard
	 */
	public applyScoreCardByCard(checkoutKey: string, scoreCard: ScoreCard) {
		return this.applyScoreCard(`/api/v1/checkouts/${checkoutKey}/scorecard`, scoreCard);
	}

	/**
	 * @returns {Observable<Checkout>}
	 *
	 * @description
	 * Applies a scorecard by phone number or ScoreCard number
	 *
	 * NOTE: Retry strategy will ignore the following status codes and not perform any retries:
	 * 400 (Bad Request), 410 (Checkout Already Submitted), 422 (Checkout Redirect Required), 426 (Invalid Checkout Key)
	 *
	 * @param scorecardUrl
	 * @param scoreCard
	 */
	public applyScoreCard(scorecardUrl: string, scoreCard?: ScoreCard): Observable<Checkout> {
		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		// Override default Timeout specified in HttpInterceptor
		httpOptions.headers = httpOptions.headers.set(CustomHttpHeaders.X_TIMEOUT, `${ DCSG_CONSTANTS.timeouts.xshort }`);

		// Make the HTTP Request
		return this.http.put<Checkout>(scorecardUrl, scoreCard, httpOptions).pipe(
			retryWhen(httpScalingRetryStrategy({
				maxRetryAttempts: this.DEFAULT_HTTP_RETRIES,
				scalingDuration: this.DEFAULT_HTTP_RETRY_DURATION,
				excludedStatusCodes: [400, 410, 422, 426],
				logger: this.logger
			}))
		);
	}

	/**
	 * @returns {Observable<Checkout>}
	 *
	 * @description
	 * Removes a ScoreCard from the checkout
	 *
	 * NOTE: Retry strategy will ignore the following status codes and not perform any retries:
	 * 400 (Bad Request), 410 (Checkout Already Submitted), 422 (Checkout Redirect Required), 426 (Invalid Checkout Key)
	 *
	 * @param checkoutKey
	 */
	public deleteScoreCard(checkoutKey: string): Observable<Checkout> {
		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		// Override default Timeout specified in HttpInterceptor
		httpOptions.headers = httpOptions.headers.set(CustomHttpHeaders.X_TIMEOUT, `${ DCSG_CONSTANTS.timeouts.xshort }`);

		// Make the HTTP Request
		return this.http.delete<Checkout>(`/api/v1/checkouts/${ checkoutKey }/scorecard`, httpOptions).pipe(
			retryWhen(httpScalingRetryStrategy({
				maxRetryAttempts: this.DEFAULT_HTTP_RETRIES,
				scalingDuration: this.DEFAULT_HTTP_RETRY_DURATION,
				excludedStatusCodes: [400, 410, 422, 426],
				logger: this.logger
			}))
		);
	}

	/**
	 * @returns {Observable<string>}
	 *
	 * @description
	 * Function that returns client ip
	 */
	public getIp$(retries = 2): Observable<string> {
		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		// Override default Timeout specified in HttpInterceptor
		httpOptions.headers = httpOptions.headers.set(CustomHttpHeaders.X_TIMEOUT, `${ DCSG_CONSTANTS.timeouts.xshort }`);

		// Make the HTTP Request
		return this.http.get('/api/v1/ip', httpOptions).pipe(
			map((response: any) => {
				return response.clientIp;
			}),
			retry(retries)
		);
	}

	/**
	 * @returns {Observable<Checkout>}
	 *
	 * @description
	 * Adds the Gifting details to the checkout
	 *
	 * NOTE: Retry strategy will ignore the following status codes and not perform any retries:
	 * 400 (Bad Request), 406 (Sensitive Language), 410 (Checkout Already Submitted), 422 (Checkout Redirect Required), 426 (Invalid Checkout Key)
	 *
	 * @param checkoutKey
	 * @param gift
	 */
	submitGifting(checkoutKey: string, gift: IGift, checkoutFlow: CheckoutFlow): Observable<Checkout> {
		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		if (CheckoutFlow.MODERN === checkoutFlow) {
			httpOptions.headers = this.setDefaultModernHttpHeaders(httpOptions);
		}

		// Override default Timeout specified in HttpInterceptor
		httpOptions.headers = httpOptions.headers.set(CustomHttpHeaders.X_TIMEOUT, `${ DCSG_CONSTANTS.timeouts.xshort }`);

		// Make the HTTP Request
		return this.http.put<Checkout>(`/api/v1/checkouts/${ checkoutKey }/v2/gifts/details`, gift, httpOptions).pipe(
			retryWhen(httpScalingRetryStrategy({
				maxRetryAttempts: this.DEFAULT_HTTP_RETRIES,
				scalingDuration: this.DEFAULT_HTTP_RETRY_DURATION,
				excludedStatusCodes: [400, 406, 410, 422, 426],
				logger: this.logger
			}))
		);
	}

	/**
	 * @returns {Observable<Checkout>}
	 *
	 * @description
	 * Removes the Gifting details from the checkout
	 *
	 * NOTE: Retry strategy will ignore the following status codes and not perform any retries:
	 * 400 (Bad Request), 410 (Checkout Already Submitted), 422 (Checkout Redirect Required), 426 (Invalid Checkout Key)
	 *
	 * NOTE: HTTP Status 406 means the gift message contains sensitive language, and we want to display a message to the user
	 * @param checkoutKey
	 */
	deleteGifting(checkoutKey: string, checkoutFlow: CheckoutFlow): Observable<Checkout> {
		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		if (CheckoutFlow.MODERN === checkoutFlow) {
			httpOptions.headers = this.setDefaultModernHttpHeaders(httpOptions);
		}

		// Override default Timeout specified in HttpInterceptor
		httpOptions.headers = httpOptions.headers.set(CustomHttpHeaders.X_TIMEOUT, `${ DCSG_CONSTANTS.timeouts.xshort }`);

		// Make the HTTP Request
		return this.http.delete<Checkout>(`/api/v1/checkouts/${ checkoutKey }/v2/gifts`, httpOptions).pipe(
			retryWhen(httpScalingRetryStrategy({
				maxRetryAttempts: this.DEFAULT_HTTP_RETRIES,
				scalingDuration: this.DEFAULT_HTTP_RETRY_DURATION,
				excludedStatusCodes: [400, 410, 422, 426],
				logger: this.logger
			}))
		);
	}

	/**
	 * @returns {Observable<Checkout>}
	 *
	 * @description
	 * Enables gifting for the checkout
	 *
	 * NOTE: Retry strategy will ignore the following status codes and not perform any retries:
	 * 400 (Bad Request), 410 (Checkout Already Submitted), 422 (Checkout Redirect Required), 426 (Invalid Checkout Key)
	 *
	 * @param checkoutKey
	 */
		enableGifting(checkoutKey: string, checkoutFlow: CheckoutFlow): Observable<Checkout> {
			// HTTP Options
			const httpOptions = {
				headers: this.DEFAULT_HTTP_HEADERS,
				withCredentials: true
			};

			if (CheckoutFlow.MODERN === checkoutFlow) {
				httpOptions.headers = this.setDefaultModernHttpHeaders(httpOptions);
			}

			// Override default Timeout specified in HttpInterceptor
			httpOptions.headers = httpOptions.headers.set(CustomHttpHeaders.X_TIMEOUT, `${ DCSG_CONSTANTS.timeouts.xshort }`);

			// Make the HTTP Request
			return this.http.put<Checkout>(`/api/v1/checkouts/${ checkoutKey }/v2/gifts`, null, httpOptions).pipe(
				retryWhen(httpScalingRetryStrategy({
					maxRetryAttempts: this.DEFAULT_HTTP_RETRIES,
					scalingDuration: this.DEFAULT_HTTP_RETRY_DURATION,
					excludedStatusCodes: [400, 410, 422, 426],
					logger: this.logger
				}))
			);
		}

		/**
		 * @returns {Observable<Checkout>}
		 *
		 * @description
		 * Adds gifting to the checkout on Confirmation Page
		 *
		 * NOTE: Retry strategy will ignore the following status codes and not perform any retries:
		 * 400 (Bad Request), 410 (Checkout Already Submitted), 422 (Checkout Redirect Required), 426 (Invalid Checkout Key)
		 * TODO -- Potentially add status codes after we start logging failures so we can capture additional HTTP status codes since we aren't capturing today
		 *
		 * @param checkoutKey
		 * @param gift
		 */
		submitGiftingConfirmation(checkoutKey: string, gift: IGift, checkoutFlow: CheckoutFlow): Observable<Checkout> {
			// HTTP Options
			const httpOptions = {
				headers: this.DEFAULT_HTTP_HEADERS,
				withCredentials: true
			};

			if (CheckoutFlow.MODERN === checkoutFlow) {
				httpOptions.headers = this.setDefaultModernHttpHeaders(httpOptions);
			}

			// Make the HTTP Request
			return this.http.post<Checkout>(`/api/v1/checkouts/${ checkoutKey }/gifts`, gift, httpOptions).pipe(
				retryWhen(httpScalingRetryStrategy({
					maxRetryAttempts: this.DEFAULT_HTTP_RETRIES,
					scalingDuration: this.DEFAULT_HTTP_RETRY_DURATION,
					excludedStatusCodes: [400, 410, 422, 426],
					logger: this.logger
				}))
			);
		}

	/**
	 * @returns {Observable<Checkout>}
	 *
	 * @description
	 * Adds a proxy pickup person to the checkout
	 *
	 * NOTE: Retry strategy will ignore the following status codes and not perform any retries:
	 * 400 (Bad Request), 410 (Checkout Already Submitted), 422 (Checkout Redirect Required), 426 (Invalid Checkout Key)
	 *
	 * @param checkoutKey
	 * @param proxyData
	 */
	public addProxyPickup$(checkoutKey: string, proxyData: IProxy, checkoutFlow: string): Observable<Checkout> {
		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		// Override default Timeout specified in HttpInterceptor
		httpOptions.headers = httpOptions.headers.set(CustomHttpHeaders.X_TIMEOUT, `${ DCSG_CONSTANTS.timeouts.xshort }`);

		if (CheckoutFlow.MODERN === checkoutFlow) {
			httpOptions.headers = this.setDefaultModernHttpHeaders(httpOptions);
		}

		// Make the HTTP Request
		return this.http.put<Checkout>(`/api/v1/checkouts/${ checkoutKey }/proxy-pickup`, proxyData, httpOptions).pipe(
			retryWhen(httpScalingRetryStrategy({
				maxRetryAttempts: this.DEFAULT_HTTP_RETRIES,
				scalingDuration: this.DEFAULT_HTTP_RETRY_DURATION,
				excludedStatusCodes: [400, 410, 422, 426],
				logger: this.logger
			}))
		);
	}

	/**
	 * @returns {Observable<Checkout>}
	 *
	 * @description
	 * Removes a proxy pickup person from the checkout
	 *
	 * NOTE: Retry strategy will ignore the following status codes and not perform any retries:
	 * 400 (Bad Request), 410 (Checkout Already Submitted), 422 (Checkout Redirect Required), 426 (Invalid Checkout Key)
	 *
	 * @param checkoutKey
	 */
	public deleteProxyPickup$(checkoutKey: string, checkoutFlow: string): Observable<Checkout> {
		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		// Override default Timeout specified in HttpInterceptor
		httpOptions.headers = httpOptions.headers.set(CustomHttpHeaders.X_TIMEOUT, `${ DCSG_CONSTANTS.timeouts.xshort }`);

		if (CheckoutFlow.MODERN === checkoutFlow) {
			httpOptions.headers = this.setDefaultModernHttpHeaders(httpOptions);
		}

		// Make the HTTP Request
		return this.http.delete<Checkout>(`/api/v1/checkouts/${ checkoutKey }/proxy-pickup`, httpOptions).pipe(
			retryWhen(httpScalingRetryStrategy({
				maxRetryAttempts: this.DEFAULT_HTTP_RETRIES,
				scalingDuration: this.DEFAULT_HTTP_RETRY_DURATION,
				excludedStatusCodes: [400, 410, 422, 426],
				logger: this.logger
			}))
		);
	}

	/**
	 * @returns {Observable<any>}
	 *
	 * @description
	 * Adds same day delivery tip to the checkout object
	 *
	 * @param checkoutKey
	 * @param tipAmount
	 */
	public submitTipAmount$(checkoutKey: string, tipAmount: string): Observable<any> {
		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		const tip = {
			tip: tipAmount
		};

		// Make the HTTP Request
		return this.http.put<any>(`/api/v1/checkouts/${ checkoutKey }/v1/tip`, tip, httpOptions).pipe(
			map((data: any) => {
				return data;
			})
		);
	}

	/**
	 * @returns {Observable<any>}
	 *
	 * @description
	 * Authorizes payment through PayGate with Independence Pay flow
	 *
	 * @param checkoutKey
	 * @param paygateAuthReference
	 */
	public authorizePaygate(checkoutKey: string, paygateAuthReference: string, paymentType: string, savedPaymentIndicator: string): Observable<any> {
		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		const body = {
			paygateReference: paygateAuthReference,
			paymentType,
			savedPaymentIndicator
		};

		// Make the HTTP Request
		return this.http.put<any>(`/api/v2/checkouts/${ checkoutKey }/payment/payment-processor/paygate`, body, httpOptions).pipe(
			map((data: any) => {
				return data;
			})
		);
	}

	//endregion

	//region Modern Checkout

	/**
	 * @returns {Observable<Checkout>}
	 *
	 * @description
	 * Create a full checkout object
	 */
	public createModernCheckout$(): Observable<Checkout> {
		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		// Override default Timeout specified in HttpInterceptor
		httpOptions.headers = httpOptions.headers.set(CustomHttpHeaders.X_TIMEOUT, `${ DCSG_CONSTANTS.timeouts.medium }`);

		// set modern checkout http header to identify requests that we want to run translation on
		httpOptions.headers = this.setDefaultModernHttpHeaders(httpOptions);

		// Make the HTTP Request
		return this.http.post<Checkout>('/api/v1/checkouts', null, httpOptions);
	}

	/**
	 * @returns {Observable<Checkout>}
	 *
	 * @description
	 * Gets an existing checkout object by checkout key.
	 * This was changed to a PATCH method in modern checkout.
	 *
	 * @param checkoutKey
	 */
	public getModernCheckout$(checkoutKey: string): Observable<Checkout> {
		// Default URL
		const url = `/api/v1/checkouts/${ checkoutKey }`;

		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		// Override default Timeout specified in HttpInterceptor
		httpOptions.headers = httpOptions.headers.set(CustomHttpHeaders.X_TIMEOUT, `${ DCSG_CONSTANTS.timeouts.medium }`);

		// set modern checkout http header to identify requests that we want to run translation on
		httpOptions.headers = this.setDefaultModernHttpHeaders(httpOptions);

		httpOptions.headers = httpOptions.headers.set('Content-Length', '0'); // Default to 0 in case users browser doesn't provide content-length (All major browsers typically do)

		// Make the HTTP Request
		return this.http.patch<Checkout>(url, {}, httpOptions).pipe(
			retry(this.DEFAULT_HTTP_RETRIES)
		);
	}

	/**
	 * @returns {Observable<Checkout>}
	 *
	 * @description
	 * Gets an existing submitted checkout object by checkout key.
	 *
	 * @param checkoutKey
	 */
	public getSubmittedModernCheckout$(checkoutKey: string): Observable<Checkout> {
		// Default URL
		const url = `/api/v1/checkouts/submitted/${ checkoutKey }`;

		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		// Override default Timeout specified in HttpInterceptor
		httpOptions.headers = httpOptions.headers.set(CustomHttpHeaders.X_TIMEOUT, `${ DCSG_CONSTANTS.timeouts.medium }`);

		// set modern checkout http header to identify requests that we want to run translation on
		httpOptions.headers = this.setDefaultModernHttpHeaders(httpOptions);

		// Make the HTTP Request
		return this.http.get<Checkout>(url, httpOptions).pipe(
			retry(this.DEFAULT_HTTP_RETRIES)
		);
	}

	/**
	 * @returns {Observable<Checkout>}
	 *
	 * @description
	 * Function that calls the Checkout Engine integrateCheckoutAddresses API to sync addresses to commerce.
	 * This coarse-grain API accepts CheckoutKey as path parameter
	 *
	 * NOTE: Retry strategy will ignore the following status codes and not perform any retries:
	 * 400 (Bad Request), 410 (Checkout Already Submitted), 422 (Checkout Redirect Required), 426 (Invalid Checkout Key), 431 (ShipMode Restriction), 451 (Shipping Restriction)
	 *
	 * @param checkoutKey
	 * @param addressType
	 * @param userAddress
	 */
	public addAndFinalizeAddress$(checkoutKey: string, addressType: string, userAddress: AddressV2): Observable<Checkout> {
		// Default URL
		let url = `/api/v1/checkouts/${ checkoutKey }/addresses`;

		if (addressType === 'billing') {
			url = `${ url }?type=billing`;
		} else if (addressType === 'shipping') {
			url = `${ url }?type=shipping`;
		}

		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		// set modern checkout http header to identify requests that we want to run translation on
		httpOptions.headers = this.setDefaultModernHttpHeaders(httpOptions);

		// Make the HTTP Request
		return this.http.post<Checkout>(url, userAddress, httpOptions).pipe(
			retryWhen(httpScalingRetryStrategy({
				maxRetryAttempts: this.DEFAULT_HTTP_RETRIES,
				scalingDuration: this.DEFAULT_HTTP_RETRY_DURATION,
				excludedStatusCodes: [400, 410, 422, 426, 431, 451],
				logger: this.logger
			}))
		);
	}

	/**
	 * @returns {Observable<Checkout>}
	 *
	 * @description
	 * Finalizes/Submits the order
	 *
	 * NOTE: Retry strategy will ignore the following status codes and not perform any retries:
	 * 400 (Bad Request), 410 (Checkout Already Submitted), 422 (Checkout Redirect Required), 426 (Invalid Checkout Key)
	 *
	 * @param checkoutKey
	 * @param flow
	 */
	public submitCheckout$(checkoutKey: string, flow?: string): Observable<Checkout> {
		// Default URL
		let url = `/api/v1/checkouts/${ checkoutKey }`;

		// Check if the flow is specified, e.g. PayPal
		if (typeof flow === 'string' && flow.trim() !== '') {
			url = `${ url }?flow=${ flow }`;
		}

		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		// Override default Timeout specified in HttpInterceptor
		httpOptions.headers = httpOptions.headers.set(CustomHttpHeaders.X_TIMEOUT, `${ DCSG_CONSTANTS.timeouts.long }`);

		// set modern checkout http header to identify requests that we want to run translation on
		httpOptions.headers = this.setDefaultModernHttpHeaders(httpOptions);

		// Make the HTTP Request
		return this.http.post<Checkout>(url, null, httpOptions).pipe(
			retryWhen(httpScalingRetryStrategy({
				maxRetryAttempts: this.DEFAULT_HTTP_RETRIES,
				scalingDuration: this.DEFAULT_HTTP_RETRY_DURATION,
				excludedStatusCodes: [400, 410, 422, 426],
				logger: this.logger
			}))
		);
	}

	/**
	 * @returns {Observable<Checkout>}
	 *
	 * @description
	 * Adds a gift card to the checkout
	 *
	 * NOTE: Retry strategy will ignore the following status codes and not perform any retries:
	 * 400 (Bad Request), 406 (The Gift Card Already Applied to the Order), 417 ($0 Balance Error)
	 *
	 * @param checkoutKey
	 * @param cardNumber
	 * @param pin
	 */
	public applyModernGiftCard(checkoutKey: string, cardNumber: string, pin: string): Observable<Checkout> {
		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		// Override default Timeout specified in HttpInterceptor
		httpOptions.headers = httpOptions.headers.set(CustomHttpHeaders.X_TIMEOUT, `${ DCSG_CONSTANTS.timeouts.short }`);

		// set modern checkout http header to identify requests that we want to run translation on
		httpOptions.headers = this.setDefaultModernHttpHeaders(httpOptions);

		const giftCardBody = {
			cardNumber,
			pin
		};

		// Make the HTTP Request
		return this.http.post<Checkout>(`/api/v1/checkouts/${ checkoutKey }/gift-cards`, giftCardBody, httpOptions).pipe(
			retryWhen(httpScalingRetryStrategy({
				maxRetryAttempts: this.DEFAULT_HTTP_RETRIES,
				scalingDuration: this.DEFAULT_HTTP_RETRY_DURATION,
				excludedStatusCodes: [400, 406, 417],
				logger: this.logger
			}))
		);
	}


	/**
	 * @returns {Observable<Checkout>}
	 *
	 * @description
	 * Removes a gift card from the checkout
	 *
	 * NOTE: Retry strategy will ignore the following status codes and not perform any retries:
	 * 400 (Bad Request), 406 (Not a valid gift card to delete)
	 * TODO -- Potentially add status codes after we start logging failures so we can capture additional HTTP status codes since we aren't capturing today
	 *
	 * @param checkoutKey
	 * @param index
	 */
	public removeModernGiftCard(checkoutKey: string, index: string): Observable<Checkout> {
		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		// Override default Timeout specified in HttpInterceptor
		httpOptions.headers = httpOptions.headers.set(CustomHttpHeaders.X_TIMEOUT, `${ DCSG_CONSTANTS.timeouts.short }`);

		// set modern checkout http header to identify requests that we want to run translation on
		httpOptions.headers = this.setDefaultModernHttpHeaders(httpOptions);

		// Make the HTTP Request
		return this.http.delete<Checkout>(`/api/v1/checkouts/${ checkoutKey }/gift-cards/${ index }`, httpOptions).pipe(
			retryWhen(httpScalingRetryStrategy({
				maxRetryAttempts: this.DEFAULT_HTTP_RETRIES,
				scalingDuration: this.DEFAULT_HTTP_RETRY_DURATION,
				excludedStatusCodes: [400, 406],
				logger: this.logger
			}))
		);
	}

	/**
	 * @returns {Observable<any>}
	 *
	 * @description
	 * Adds same day delivery tip to the checkout object
	 *
	 * @param checkoutKey
	 * @param tipAmount
	 */
	public submitModernTipAmount$(checkoutKey: string, tipAmount: string): Observable<any> {
		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		// Override default Timeout specified in HttpInterceptor
		httpOptions.headers = httpOptions.headers.set(CustomHttpHeaders.X_TIMEOUT, `${ DCSG_CONSTANTS.timeouts.short }`);

		// set modern checkout http header to identify requests that we want to run translation on
		httpOptions.headers = this.setDefaultModernHttpHeaders(httpOptions);

		const tip = {
			tip: tipAmount
		};

		// Make the HTTP Request
		return this.http.put<any>(`/api/v1/checkouts/${ checkoutKey }/tip`, tip, httpOptions).pipe(
			map((data: any) => {
				return data;
			})
		);
	}

		/**
		 * @returns {Observable<Checkout>}
		 *
		 * @description
		 * Applies a scorecard by phone number
		 *
		 * NOTE: Retry strategy will ignore the following status codes and not perform any retries:
		 * 400 (Bad Request), 410 (Checkout Already Submitted), 422 (Checkout Redirect Required), 426 (Invalid Checkout Key)
		 *
		 * @param checkoutKey
		 * @param phoneNumber
		 */
		public applyModernScoreCardByPhone(checkoutKey: string, phoneNumber: string): Observable<Checkout> {
			// HTTP Options
			const httpOptions = {
				headers: this.DEFAULT_HTTP_HEADERS,
				withCredentials: true
			};

			// Override default Timeout specified in HttpInterceptor
			httpOptions.headers = httpOptions.headers.set(CustomHttpHeaders.X_TIMEOUT, `${ DCSG_CONSTANTS.timeouts.xshort }`);

			// set modern checkout http header to identify requests that we want to run translation on
			httpOptions.headers = this.setDefaultModernHttpHeaders(httpOptions);

			const body = { customerIdentifier: btoa(phoneNumber) };

			// Make the HTTP Request
			return this.http.put<Checkout>(`/api/v1/checkouts/${checkoutKey}/scorecard`, body, httpOptions).pipe(
				retryWhen(httpScalingRetryStrategy({
					maxRetryAttempts: this.DEFAULT_HTTP_RETRIES,
					scalingDuration: this.DEFAULT_HTTP_RETRY_DURATION,
					excludedStatusCodes: [400, 410, 422, 426],
					logger: this.logger
				}))
			);
		}

				/**
		 * @returns {Observable<Checkout>}
		 *
		 * @description
		 * Applies a scorecard by scorecard number
		 *
		 * NOTE: Retry strategy will ignore the following status codes and not perform any retries:
		 * 400 (Bad Request), 410 (Checkout Already Submitted), 422 (Checkout Redirect Required), 426 (Invalid Checkout Key)
		 *
		 * @param checkoutKey
		 * @param scoreCard
		 */
			public applyModernScoreCardByCardNumber(checkoutKey: string, scoreCard: ScoreCard): Observable<Checkout> {
				// HTTP Options
				const httpOptions = {
					headers: this.DEFAULT_HTTP_HEADERS,
					withCredentials: true
				};

				// Override default Timeout specified in HttpInterceptor
				httpOptions.headers = httpOptions.headers.set(CustomHttpHeaders.X_TIMEOUT, `${ DCSG_CONSTANTS.timeouts.xshort }`);

				// set modern checkout http header to identify requests that we want to run translation on
				httpOptions.headers = this.setDefaultModernHttpHeaders(httpOptions);

				// Make the HTTP Request
				return this.http.put<Checkout>(`/api/v1/checkouts/${checkoutKey}/scorecard`, scoreCard, httpOptions).pipe(
					retryWhen(httpScalingRetryStrategy({
						maxRetryAttempts: this.DEFAULT_HTTP_RETRIES,
						scalingDuration: this.DEFAULT_HTTP_RETRY_DURATION,
						excludedStatusCodes: [400, 410, 422, 426],
						logger: this.logger
					}))
				);
			}
		/**
		 * @returns {Observable<Checkout>}
		 *
		 * @description
		 * Removes a ScoreCard from modern checkout
		 *
		 * NOTE: Retry strategy will ignore the following status codes and not perform any retries:
		 * 400 (Bad Request), 410 (Checkout Already Submitted), 422 (Checkout Redirect Required), 426 (Invalid Checkout Key)
		 *
		 * @param checkoutKey
		 */
		public deleteModernScoreCard(checkoutKey: string): Observable<Checkout> {
			// HTTP Options
			const httpOptions = {
				headers: this.DEFAULT_HTTP_HEADERS,
				withCredentials: true
			};

			// Override default Timeout specified in HttpInterceptor
			httpOptions.headers = httpOptions.headers.set(CustomHttpHeaders.X_TIMEOUT, `${ DCSG_CONSTANTS.timeouts.xshort }`);

			// set modern checkout http header to identify requests that we want to run translation on
			httpOptions.headers = this.setDefaultModernHttpHeaders(httpOptions);

			// Make the HTTP Request
			return this.http.delete<Checkout>(`/api/v1/checkouts/${ checkoutKey }/scorecard`, httpOptions).pipe(
				retryWhen(httpScalingRetryStrategy({
					maxRetryAttempts: this.DEFAULT_HTTP_RETRIES,
					scalingDuration: this.DEFAULT_HTTP_RETRY_DURATION,
					excludedStatusCodes: [400, 410, 422, 426],
					logger: this.logger
				}))
			);
		}

	private setDefaultModernHttpHeaders(request: any): HttpHeaders {
		let httpHeaders = request.headers;
		httpHeaders = httpHeaders.set(CustomHttpHeaders.X_CHK_MOD, 'true');
		httpHeaders = httpHeaders.set(CustomHttpHeaders.CHK, '2');
		return httpHeaders;
	}

	//endregion
}
